import { FontAwesomeIcon } from '@appcharge/shared-ui';
import {
  EPageTitles,
  EPublisherSolutionModel,
  EPublisherSolutionModelSubType,
  Portal,
  Project,
  Store
} from 'common/contracts';
import { UserDetails } from 'pages/Login/Login.types';
import { localStorageUtil } from 'utils/localStorageUtil';

import { User } from '../../common/contracts';
import { IPermissions } from '../../common/permissions.types';
import { EFeatureFlag, ELocalStorageKeys } from '../../constants/enums';

import {
  companyLevelNavItems,
  pageFeatureFlagMap,
  paymentLinkProjectNavItems,
  portalLevelNavItems,
  portalLevelStoreNavItems,
  projectLevelNavItems,
  superAdminAllowedPages
} from './constants';
import {
  ESideBarContext,
  ESidebarGroupKey,
  SideBarGroup,
  SideBarItem
} from './types';

export const generateProjectNavItems = (projects: Project[]): SideBarItem[] => {
  return projects
    .sort((a, b) => a.publisherName.localeCompare(b.publisherName))
    .map((project: any) => ({
      id: project.publisherId,
      title: project.publisherName,
      url: `project/${project.publisherId}`,
      icon: getIconByContext(
        ESideBarContext.PROJECT,
        project.projectType === EPublisherSolutionModel.CHECKOUT,
        project.projectSubType
      )
    }));
};

export const generatePortalNavItems = (portals: Portal[]): SideBarItem[] => {
  return portals
    .sort((a, b) => a.portalName.localeCompare(b.portalName))
    .map((portal: any) => ({
      id: portal.portalId,
      title: portal.portalName,
      url: `portal/${portal.portalId}/settings`,
      icon: getIconByContext(
        ESideBarContext.PORTAL,
        false,
        undefined,
        portal.isParkingDomain
      )
    }));
};

export const generateStarredNavItems = (
  projects: Project[],
  portals: Portal[]
): SideBarItem[] => {
  const starredIds =
    localStorageUtil.get<string[]>(ELocalStorageKeys.STARRED_PROJECTS) || [];

  const starredProjects = projects
    .filter((project) => starredIds.includes(project.publisherId))
    .map((project: any) => ({
      id: project.publisherId,
      title: project.publisherName,
      url: `project/${project.publisherId}`,
      icon: getIconByContext(
        ESideBarContext.PROJECT,
        project.projectType === EPublisherSolutionModel.CHECKOUT,
        project.projectSubType
      )
    }));

  const starredPortals = portals
    .filter((portal) => starredIds.includes(portal.portalId))
    .map((portal: Portal) => ({
      id: portal.portalId,
      title: portal.portalName,
      url: `portal/${portal.portalId}/settings`,
      icon: getIconByContext(
        ESideBarContext.PORTAL,
        false,
        undefined,
        portal.isParkingDomain
      )
    }));

  const allStarredItems = [...starredPortals, ...starredProjects];

  return allStarredItems.sort((a, b) => {
    const aIndex = starredIds.indexOf(a.id);
    const bIndex = starredIds.indexOf(b.id);
    return aIndex - bIndex;
  });
};

export const generatePortalLevelStoreNavItems = (
  stores: Store[]
): SideBarItem[] => {
  return stores
    .sort((a, b) => a.storeName.localeCompare(b.storeName))
    .map((store: any) => ({
      id: store.storeId,
      title: store.storeName,
      url: `store/${store.storeId}`,
      icon: getIconByContext(ESideBarContext.PORTAL_LEVEL_STORE)
    }));
};

export const filterGroupsByPermissions = (
  groups: SideBarGroup[],
  permissions: IPermissions,
  currentProjectId?: string
): SideBarGroup[] => {
  if (!groups || groups.length < 1) {
    return [];
  }

  if (!permissions) {
    return [];
  }

  const filteredGroups = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        const requiredPermissions = item.permissions || [];
        const hasNoRequiredPermissions = requiredPermissions.length === 0;

        let hasAllRequiredPermissions = false;
        if (!currentProjectId) {
          // Check for each permission if the user has access to at least in one project
          hasAllRequiredPermissions = Object.values(permissions).some(
            (projectPermissions) => {
              return requiredPermissions.every((permission) =>
                Object.keys(projectPermissions).includes(permission)
              );
            }
          );
        } else {
          const hasLocalizationPermission = Boolean(
            permissions[currentProjectId].offers?.includes('manage')
          );
          hasAllRequiredPermissions = requiredPermissions.every(
            (permission) => {
              return item.title === 'Localization'
                ? hasLocalizationPermission
                : permissions[currentProjectId][permission]?.includes('view') ||
                    permissions[currentProjectId][permission]?.includes(
                      'manage'
                    );
            }
          );
        }

        return hasNoRequiredPermissions || hasAllRequiredPermissions;
      })
    }))
    .filter((group) => group.items.length > 0);

  return filteredGroups;
};

export const getCompanyLevelNavItems = (
  permissions: IPermissions,
  featureFlags?: Record<EFeatureFlag, boolean>,
  userDetails?: UserDetails | null
): SideBarGroup[] => {
  const permissionsFilteredGroups = filterGroupsByPermissions(
    companyLevelNavItems,
    permissions
  );

  // Filter the company level nav items by feature flags
  const filteredGroups = permissionsFilteredGroups.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      const requiredFlag = pageFeatureFlagMap[item.title as EPageTitles];
      if (!requiredFlag) {
        return true;
      }
      return !!featureFlags?.[requiredFlag as EFeatureFlag];
    })
  }));

  // Also need to filter the all projects nav items
  const projectsNavItems = userDetails?.projects
    ? generateProjectNavItems(userDetails.projects)
    : [];

  const portalsNavItems = userDetails?.portals
    ? generatePortalNavItems(userDetails.portals)
    : [];

  // Generate starred items
  const starredNavItems =
    userDetails?.projects && userDetails?.portals
      ? generateStarredNavItems(userDetails.projects, userDetails.portals)
      : [];

  const projectsGroup: SideBarGroup = {
    title: ESidebarGroupKey.Projects,
    isCollapsable: true,
    items: [...portalsNavItems, ...projectsNavItems]
  };

  const starredGroup: SideBarGroup = {
    title: ESidebarGroupKey.Starred,
    isCollapsable: true,
    items: starredNavItems
  };

  // Only include starred group if there are starred items, and place it after Projects
  const groupsToReturn =
    starredNavItems.length > 0
      ? [...filteredGroups, projectsGroup, starredGroup]
      : [...filteredGroups, projectsGroup];

  return groupsToReturn;
};

export const getProjectLevelNavItems = ({
  permissions,
  projectId,
  featureFlags,
  currentProjectSubType,
  hasSingleProject,
  isSuperAdmin
}: {
  permissions: IPermissions;
  projectId: string;
  featureFlags?: Record<EFeatureFlag, boolean>;
  currentProjectSubType?: EPublisherSolutionModelSubType | null;
  hasSingleProject: boolean;
  isSuperAdmin?: boolean;
}): SideBarGroup[] => {
  const initialNavItems =
    currentProjectSubType === EPublisherSolutionModelSubType.PAYMENT_LINKS
      ? paymentLinkProjectNavItems(hasSingleProject)
      : projectLevelNavItems;

  const permissionsFilteredGroups = filterGroupsByPermissions(
    initialNavItems,
    permissions,
    projectId
  );

  if (!featureFlags) {
    return permissionsFilteredGroups;
  }

  const filteredItems = permissionsFilteredGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (
          isSuperAdmin &&
          superAdminAllowedPages.includes(item.title as EPageTitles)
        ) {
          return true;
        }
        const requiredFlag = pageFeatureFlagMap[item.title as EPageTitles];
        if (!requiredFlag) {
          return true;
        }
        return !!featureFlags[requiredFlag];
      })
    }))
    .filter((group) => group.items.length > 0);

  return filteredItems;
};

export const getPortalLevelNavItems = (
  permissions: IPermissions,
  portalId?: string,
  userDetails?: UserDetails | null
): SideBarGroup[] => {
  // todo: filter by permissions when defined
  if (!portalId) {
    return [];
  }

  if (!userDetails) {
    console.warn(
      'getPortalLevelNavItems: userDetails is required. Use useUserDetails() hook to get user data from React Query.'
    );
    return [];
  }

  const assignedStores = userDetails?.portals?.find(
    (portal) => portal.portalId === portalId
  )?.stores;

  const portalLevelStoreNavItems = assignedStores
    ? generatePortalLevelStoreNavItems(assignedStores)
    : [];

  const storesGroup: SideBarGroup = {
    title: ESidebarGroupKey.Stores,
    isCollapsable: true,
    items: portalLevelStoreNavItems
  };

  return [...portalLevelNavItems, storesGroup];
};

export const getPortalLevelStoreNavItems = ({
  featureFlags,
  isSuperAdmin
}: {
  featureFlags?: Record<string, unknown>;
  isSuperAdmin?: boolean;
} = {}): SideBarGroup[] => {
  if (!featureFlags) {
    return portalLevelStoreNavItems;
  }

  return portalLevelStoreNavItems
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (
          isSuperAdmin &&
          superAdminAllowedPages.includes(item.title as EPageTitles)
        ) {
          return true;
        }
        const requiredFlag = pageFeatureFlagMap[item.title as EPageTitles];
        if (!requiredFlag) {
          return true;
        }
        return !!featureFlags[requiredFlag as EFeatureFlag];
      })
    }))
    .filter((group) => group.items.length > 0);
};

export const getIconByContext = (
  context: ESideBarContext,
  isCheckout: boolean = false,
  projectSubType?: EPublisherSolutionModelSubType,
  isParkingDomain?: boolean
) => {
  switch (context) {
    case ESideBarContext.PORTAL:
      if (isParkingDomain) {
        return (
          <FontAwesomeIcon
            icon="fa-light fa-money-check-dollar"
            className="p-3 rounded-full bg-sky-100 text-zinc-600 text-[12px]"
          />
        );
      }
      return (
        <FontAwesomeIcon
          icon="fa-regular fa-table-layout"
          className="p-3 rounded-full bg-violet-100 text-zinc-600 text-[12px]"
        />
      );
    case ESideBarContext.PROJECT:
      if (isCheckout) {
        // Check if it's a payment link
        if (projectSubType === EPublisherSolutionModelSubType.PAYMENT_LINKS) {
          return (
            <FontAwesomeIcon
              icon="fa-regular fa-square-up-right"
              className="p-3 rounded-full bg-zinc-200 text-zinc-600 text-[12px]"
            />
          );
        }
        // Check if it's side loading
        if (projectSubType === EPublisherSolutionModelSubType.SIDE_LOADING) {
          return (
            <FontAwesomeIcon
              icon="fa-regular fa-arrow-down-from-arc"
              className="p-3 rounded-full bg-orange-200 text-zinc-600 text-[12px]"
            />
          );
        }
        // Otherwise it's a web checkout
        return (
          <FontAwesomeIcon
            icon="fa-regular fa-credit-card"
            className="p-3 rounded-full bg-amber-100 text-zinc-600 text-[12px]"
          />
        );
      } else {
        return (
          <FontAwesomeIcon
            icon="fa-regular fa-store"
            className="p-3 rounded-full bg-green-200 text-zinc-600 text-[12px]"
          />
        );
      }
    case ESideBarContext.PORTAL_LEVEL_STORE:
      return (
        <FontAwesomeIcon
          icon="fa-regular fa-store"
          className="p-3 rounded-full bg-green-200 text-zinc-600 text-[12px]"
        />
      );

    default:
      return null;
  }
};
